import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import { UploadSvg } from "../../assets/images/svg/Upload";
import { Upload } from "antd";
import { observer } from "mobx-react";
import { applicationStore } from "src/store/ApplicationStore";
import { toast } from "react-toastify";
import logger from "src/utils/logger";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import { VerificationDocsType } from "src/enums/VerificationDocsType";
import UploadProcessingWithAnimationComponent from "src/components/UploadProcessingWithAnimationComponent";
import { useTranslation } from "react-i18next";
import { MAX_FILE_SIZE_MB } from "src/constants/fileConfig";
import Preloader from "src/components/ui/Preloader";
import FilePreviewModal from "src/components/FilePreviewModal";
import DocumentImg from "src/assets/images/DocumentImg.png";
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError';

const ACCEPTED_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
];

const getAcceptAttribute = () =>
  ACCEPTED_MIME_TYPES.map((type) => {
    if (type === "application/pdf") {
      return ".pdf";
    }
    if (type.startsWith("image/")) {
      return `.${type.split("/")[1]}`;
    }
    return type;
  }).join(",");

const SUPPORTED_FILE_TYPES_LABEL = ACCEPTED_MIME_TYPES.map((type) =>
  type === "application/pdf"
    ? "PDF"
    : type.split("/").pop()?.toUpperCase()
).join(", ");

const { Dragger } = Upload;

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

const AddressVerification = observer(() => {

  const navigate = useNavigate();
  const docs_type = VerificationDocsType.INVOICE;
  const [loading, setLoading] = useState(false);
  const [filePayload, setFilePayload] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isPreparingFile, setIsPreparingFile] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { t } = useTranslation();

  const acceptAttribute = getAcceptAttribute();

  const handleBeforeUpload = useCallback(
    async (file) => {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        toast.warning(t("addressVerification.fileTooLarge", { size: MAX_FILE_SIZE_MB }));
        return Upload.LIST_IGNORE;
      }

      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        toast.error(t("addressVerification.invalidType"));
        return Upload.LIST_IGNORE;
      }

      try {
        setIsPreparingFile(true);
        const base64Data = await convertFileToBase64(file);
        logger.log(base64Data);
        setFilePayload({
          name: file.name,
          content: base64Data,
          type: file.type,
        });
        setSelectedFileName(file.name);
        setFileList([{ uid: file.uid, name: file.name, status: "done" }]);
      } catch (error) {
        logger.error("Error processing file:", error);
        toast.error(t("addressVerification.processError"));
        return Upload.LIST_IGNORE;
      } finally {
        setIsPreparingFile(false);
      }

      return false;
    },
    [t]
  );

  const handleRemoveFile = () => {
    setFilePayload(null);
    setSelectedFileName("");
    setIsPreviewOpen(false);
    setFileList([]);
  };

  const handleSubmit = async (base64Data) => {
    const formObject = {
      docs_type,
      id_frontimage: base64Data,
    };

    try {
      const response = await workflowStepIterator({
        access_token: applicationStore.access_token,
        node_id: applicationStore.workflowNextServiceResponse.node_id,
        body: {
          ...formObject,
        },
        action_index: applicationStore.workflowNextServiceResponse.index,
        constextOfStep: {
          step: NodeTypes.ADDRESS_VERIFICATION,
          retry: applicationStore.workflowNextServiceResponse.retry,
        },
      });
      if (response.data?.service_results[0].status !== 200) {
        const rawMessage = response.data?.service_results[0]?.message;
        const parsedMessage = parseServiceErrorMessage(rawMessage);
        throw new Error(parsedMessage);
      } else {
        applicationStore.setWorkflowNextServiceResponse(response.data);
        navigate("/next", { replace: true });
      }
    } catch (error) {
      logger.error("Error uploading document:", error);
      if (error?.response?.status === 406) {
        toast.error(error?.response?.data?.detail);
      } else {
        toast.error(error?.message || t('addressVerification.notFound'));
        applicationStore.setApplicationErrorObj({
          header: t('addressVerification.stepFailedHeader'),
          description: `${error?.message} ${t('addressVerification.tryAgain')}`,
          buttonList: [
            {
              label: "Retry",
              link: `/next`,
              variant: "primaryWithoutIcon",
            },
          ],
        });
        navigate("/error-page", { replace: true });
      }
    }
  };

  const isContinueDisabled = !filePayload || isPreparingFile || loading;
  const isUploadDisabled = Boolean(filePayload) || isPreparingFile;

  const handleContinue = async () => {
    if (isPreparingFile) {
      return;
    }

    if (!filePayload) {
      toast.error(t('fileUpload.fileRequired'));
      return;
    }

    setLoading(true);
    try {
      await handleSubmit(filePayload.content);
      handleRemoveFile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <UploadProcessingWithAnimationComponent
          title={t('addressVerification.processingTitle')}
          message={t('addressVerification.processingMessage')}
        />
      ) : (
        <div className="custom-container">
          <div className="flex flex-col h-full justify-between">
            <div>
              <div className="text-left">
                <Button
                  variant="primaryBack"
                  onClick={() =>
                    navigate(`/?aid=${applicationStore.aidCode}`, {
                      replace: true,
                    })
                  }
                >
                  <LeftArrowWhite />
                </Button>
              </div>
              <div className="grid gap-4 mt-10">
                <h1 className="text-h5 font-bold text-flow-heading">
                  {t('addressVerification.title')}
                </h1>
                <p className="text-body text-Gray900">
                  {t('addressVerification.description')}
                </p>
              </div>

              <div className="mt-6">
                <Dragger
                  name="document"
                  multiple={false}
                  beforeUpload={handleBeforeUpload}
                  fileList={fileList}
                  showUploadList={false}
                  disabled={isUploadDisabled}
                  accept={acceptAttribute}
                  className={`flow-upload-dragger !rounded-[24px] !py-8 ${isUploadDisabled ? "cursor-not-allowed" : ""}`}
                  style={isUploadDisabled ? { borderStyle: "none" } : undefined}
                >
                  {isPreparingFile ? (
                    <div className="h-[180px] flex items-center justify-center">
                      <Preloader variant="sorrell" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4">
                      <UploadSvg />
                      <div className="text-center">
                        <p className="text-flow-heading text-xs">
                          {t('fileUpload.dragSubtitle')}{" "}
                          <span className="flow-upload-browse">
                            {t('fileUpload.browse')}
                          </span>
                        </p>
                        <p className="text-xs text-Gray700 mt-2">
                          {t('fileUpload.fileLimit', { size: MAX_FILE_SIZE_MB })}
                          <br />
                          {t('fileUpload.supportedTypes', {
                            types: SUPPORTED_FILE_TYPES_LABEL,
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </Dragger>
              </div>

              {selectedFileName && (
                <div className="flow-upload-file-card mt-4 flex items-center justify-between rounded-lg border border-SorrellBrown200 px-4 py-3 gap-3">
                  <Button
                    type="text"
                    htmlType="button"
                    className="flow-upload-preview-button flex items-center gap-3 text-left flex-1 h-auto p-0"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    {filePayload?.content && !filePayload.type?.includes("pdf") ? (
                      <div className="flow-upload-preview-thumbnail w-12 h-12 rounded-xl overflow-hidden bg-Gray100 flex items-center justify-center">
                        <img
                          src={filePayload.content}
                          alt={selectedFileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) :
                      <img src={DocumentImg} alt="Document" width={16} height={16} />
                    }
                    <div className="flex flex-col">
                      <span className="text-xs text-Gray900 truncate max-w-40" title={selectedFileName}>
                        {selectedFileName}
                      </span>
                      <span className="text-xs flow-upload-inline-action">
                        {t('actions.preview')}
                      </span>
                    </div>
                  </Button>
                  <Button
                    type="link"
                    htmlType="button"
                    size="small"
                    onClick={handleRemoveFile}
                    className="text-xs flow-upload-inline-action px-0"
                  >
                    {t('fileUpload.remove')}
                  </Button>
                </div>
              )}
            </div>

            <div className="grid gap-2 pb-16">
              <Button onClick={handleContinue} disabled={isContinueDisabled}>
                {t('actions.continue')}
              </Button>

              {window?.top === window?.self && (
                <Button
                  variant="secondaryWithoutIcon"
                  onClick={() => {
                    applicationStore.setContinueOnAnotherDevice(
                      "/address-verification"
                    );
                    navigate("/continue-on-another-device", {
                      replace: true,
                    });
                  }}
                >
                  {t('addressVerification.continueOnAnotherDevice')}
                </Button>
              )}
            </div>
          </div>
          <FilePreviewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            fileContent={filePayload?.content}
            fileName={selectedFileName}
            fileType={filePayload?.type}
            title={selectedFileName || t('actions.preview')}
          />
        </div>
      )}
    </>
  );
});

export default AddressVerification;
